import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { AuthModal } from '@/components/auth/AuthModal';
import { useAuth } from '@/lib/auth';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Calendar, MapPin, Users, Clock, ArrowDown, Play, Pause, Volume2, VolumeX, Anchor, Sunset, Ship, Wind, Coffee, BedDouble, Utensils, Music, Sparkles, Tent, Star, Globe, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { MetaTags } from '@/components/MetaTags';

export function AegeanExperience() {
  const { user } = useAuth();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [freezeFrameShowing, setFreezeFrameShowing] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);
  
  // Image gallery for accommodation
  const accommodationImages = [
    "https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/Catamaran%20Lodging.png",
    "https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/20240229-untitled-14-34-07.jpg",
    "https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/20240229-untitled-14-57-53-Enhanced-NR.jpg",
    "https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/20240229-untitled-16-23-54.jpg",
  ];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Mark page as loaded
  useEffect(() => {
    setPageLoaded(true);
  }, []);
  
  // Initialize Google Maps
  useEffect(() => {
    const mapElement = document.getElementById('venue-map');
    if (!mapElement || !window.google?.maps) return;

    const map = new google.maps.Map(mapElement, {
      center: { lat: 37.4415, lng: 25.3667 }, // Center on general Aegean region
      zoom: 7,
      mapTypeId: "roadmap",
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
        style: google.maps.ZoomControlStyle.SMALL
      }
    });

    // Define locations in order of journey
    const locations = [
      { position: { lat: 37.4467, lng: 25.3289 }, label: '1', title: "Mykonos - Starting point" },
      { position: { lat: 37.3964, lng: 25.2674 }, label: '2', title: "Delos - Ancient ruins" },
      { position: { lat: 36.8300, lng: 25.8877 }, label: '3', title: "Amorgos - Dramatic landscapes" },
      { position: { lat: 36.4340, lng: 28.2176 }, label: '4', title: "Rhodes - Historic fortress city" },
      { position: { lat: 38.3088, lng: 26.6337 }, label: '5', title: "Bonjuk Bay, Turkey - Festival" }
    ];
    
    // Create markers for each location with numbered labels
    locations.forEach(location => {
      new google.maps.Marker({
        position: location.position,
        map,
        label: location.label,
        title: location.title
      });
    });
    
    // Create lines to show the journey path
    const journeyPath = new google.maps.Polyline({
      path: locations.map(location => location.position),
      geodesic: true,
      strokeColor: "#FF5722",
      strokeOpacity: 0.8,
      strokeWeight: 3
    });
    
    journeyPath.setMap(map);

    // Create info window to display when marker is clicked
    const infoWindow = new google.maps.InfoWindow();

    // Add click listeners to markers
    locations.forEach((location, i) => {
      const marker = new google.maps.Marker({
        position: location.position,
        map,
        label: location.label,
        title: location.title
      });
      
      marker.addListener("click", () => {
        infoWindow.setContent(`
          <div style="padding: 8px; max-width: 200px;">
            <h3 style="margin: 0 0 8px; font-weight: bold;">${location.title.split(' - ')[0]}</h3>
            <p style="margin: 0; font-size: 14px;">${location.title.split(' - ')[1]}</p>
            <p style="margin: 5px 0 0; font-size: 12px; color: #666;">Stop ${i + 1} of ${locations.length}</p>
          </div>
        `);
        infoWindow.open(map, marker);
      });
    });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !pageLoaded) return;

    // Preload video data
    video.preload = "auto";
    
    // Handle video loading states
    const handleLoadedMetadata = () => {
      setIsVideoLoaded(true);
      
      // Start the video 2.5 seconds after the page loads
      setTimeout(() => {
        setFreezeFrameShowing(false);
        video.play()
          .then(() => setIsPlaying(true))
          .catch(err => {
            console.warn('Autoplay failed:', err);
            setIsPlaying(false);
          });
      }, 2500);
    };

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!freezeFrameShowing) { // Only auto-play if freeze frame is not showing
          video.play().catch(err => console.warn('Visibility play failed:', err));
          setIsPlaying(true);
        }
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));

    // Start loading the video
    video.load();

    // Cleanup
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
    };
  }, [pageLoaded]);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    // If we're showing the freeze frame, cancel it and play immediately
    if (freezeFrameShowing) {
      setFreezeFrameShowing(false);
    }

    if (video.paused) {
      video.play()
        .then(() => setIsPlaying(true))
        .catch(error => {
          console.error('Play failed:', error);
          setIsPlaying(false);
        });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handlePurchaseClick = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    // Open contact email in mail client
    window.location.href = "mailto:contact@space-time.travel?subject=Aegean%20Odyssey%20Inquiry&body=I'm%20interested%20in%20the%20Aegean%20Odyssey%20experience%20(May%2026-June%202,%202025).%20Please%20send%20me%20more%20information.";
  };

  const scrollToContent = () => {
    const contentSection = document.getElementById('content-section');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Image gallery navigation
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === accommodationImages.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? accommodationImages.length - 1 : prevIndex - 1
    );
  };

  return (
    <div className="relative min-h-screen bg-earth-900">
      <MetaTags 
        title="Aegean Odyssey: Sailing + Festival | rendezvous"
        description="Sail the Greek Islands and experience the Per Anhalter Festival in this unique dual adventure. May 26-June 2, 2025."
        image="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/221002_2_timbertotide%20(1).jpg"
        url="https://letsrendezvous.co/experiences/aegean"
      />

      {/* Hero Section with Video */}
      <div className="relative w-full h-[100vh] overflow-hidden pt-10">
        {/* Hero Video Container with Center Alignment */}
        <div className="absolute inset-0 top-20 flex justify-center items-center bg-earth-900 overflow-hidden">
          {/* Poster Image/Freeze Frame */}
          <div 
            className={`absolute inset-0 z-10 transition-opacity duration-500 ${freezeFrameShowing ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/221002_2_timbertotide%20(1).jpg"
              alt="Aegean Odyssey"
              className="w-full h-full object-cover object-center"
            />
          </div>
          
          {/* Video Element */}
          <video
            ref={videoRef}
            src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/short%20catemaran%20drone.mp4"
            className={`w-full h-full object-cover transition-opacity duration-1000 ${freezeFrameShowing ? 'opacity-0' : 'opacity-100'}`}
            muted={isMuted}
            loop
            playsInline
            poster="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/221002_2_timbertotide%20(1).jpg"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-earth-900/50 to-earth-900" />

          {/* Video Controls */}
          <div className="absolute bottom-10 right-10 flex items-center gap-4 z-20">
            <button
              onClick={handlePlayPause}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors"
            >
              {isPlaying && !freezeFrameShowing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
            </button>
            <button
              onClick={handleToggleMute}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors"
            >
              {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="absolute inset-0 top-20 flex flex-col items-center justify-end px-4 text-center pb-36">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display mb-6">Aegean Odyssey Voyage 1</h1>
            <p className="text-xl md:text-2xl text-sand-200 mb-8">Sailing the Greek Islands + Per Anhalter Festival</p>
            
            
          </motion.div>

          {/* Scroll Down Indicator */}
          <motion.div
            className="absolute bottom-12 left-0 right-0 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <button 
              onClick={scrollToContent}
              className="flex flex-col items-center text-sand-300 hover:text-sand-100 transition-colors"
            >
              <span className="text-sm uppercase tracking-widest mb-2">Discover</span>
              <ArrowDown className="h-6 w-6 animate-bounce" />
            </button>
            
          </motion.div>
          
        </div>
      </div>

      {/* Content Section with Tabs */}
      <div id="content-section" className="container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          {/* Sidebar Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="about" className="space-y-8">
                <TabsList className="w-full">
                  <TabsTrigger value="about">About</TabsTrigger>
                  <TabsTrigger value="itinerary">Itinerary</TabsTrigger>
                  <TabsTrigger value="accommodation">Accommodation</TabsTrigger>
                  <TabsTrigger value="location">Location</TabsTrigger>
                  <TabsTrigger value="portal">Portal of Wonders</TabsTrigger>
                  <TabsTrigger value="faq">FAQ</TabsTrigger>
                </TabsList>
                
                <TabsContent value="about" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">About the Experience</h2>
                  <div className="space-y-4 text-sand-300">
                    <p>
                      At Space-Time-Architects, we create boutique journeys that explore the world in all its dimensions. Set sail with us on a voyage where time stretches, the sea leads the way, and each day brings moments of connection, wonder, and discovery. From hidden coves to sun-soaked meals with fellow travelers, our journeys are woven with meaning, beauty, and the spirit of exploration.
                    </p>
                    <p>
                      Our journey begins in vibrant Mykonos, where we'll board our premium catamaran for four days of island-hopping through the stunning Cyclades archipelago. We'll explore ancient ruins on Delos, discover hidden coves in Amorgos, and enjoy the pristine beaches of secluded islands.
                    </p>
                    <p>
                      The second phase of our odyssey takes us to Bonjuk Bay, Turkey, for the unique Per Anhalter Festival - a creative celebration combining art, music, and spiritual experiences in a stunning natural setting.
                    </p>
                    
                    <div className="pt-4">
                      <h3 className="text-xl font-display mb-3">What's Included</h3>
                      <ul className="list-disc list-inside space-y-2">
                        <li>Cultural excursions to ancient ruins and local villages</li>
                        <li>Sunbathing at secluded beaches and hidden coves</li>
                        <li>Water sports activities and swimming in crystal clear waters</li>
                        <li>Hidden islands adventures off the typical tourist path</li>
                        <li>All meals curated by a three-star Michelin chef during sailing</li>
                        <li>Creative "flowshops" (workshops) at Per Anhalter Festival</li>
                        <li>Live music performances and artistic celebrations</li>
                        <li>All accommodations during the sailing portion</li>
                        <li>Transportation between islands and to the festival</li>
                        <li>Festival entry pass</li>
                      </ul>
                    </div>
                  </div>
                  
                  <img 
                    src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/20240301-untitled-16-43-19.jpg" 
                    alt="Greek Islands Sailing" 
                    className="w-full h-auto rounded-lg mt-6"
                  />
                </TabsContent>
                
                <TabsContent value="itinerary" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Journey Itinerary</h2>
                  <p className="text-sand-300 mb-6">
                    <strong>JOURNEY: 26 May - 2 June 2025</strong>
                  </p>
                  <div className="space-y-8">
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Sailing the Greek Islands</h3>
                      <p className="text-sand-300 mb-4">
                        We will glide across the Aegean, pausing at breathtaking islands to immerse in local culture and the sea's gentle rhythm. Our trip culminates in Bonjuk Bay for Per Anhalter - a gathering where music, art, and profound connection ignite. Join us for an odyssey that weaves adventure with stillness, play with reflection - a passage through space and time you'll never forget.
                      </p>
                    </div>
                    
                    <div className="space-y-6">
                      <h3 className="text-xl font-display mb-2">Island Destinations</h3>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/971011/pexels-photo-971011.jpeg?auto=compress&cs=tinysrgb&w=600" 
                              alt="Mykonos" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Mykonos</h4>
                            <p className="text-sand-300">Our gathering point, a threshold to new horizons. Known for its iconic windmills, vibrant nightlife, and picturesque white-washed buildings, Mykonos offers a blend of cosmopolitan energy and traditional Greek charm.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/18683748/pexels-photo-18683748/free-photo-of-delos.jpeg?auto=compress&cs=tinysrgb&w=600" 
                              alt="Delos" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Delos</h4>
                            <p className="text-sand-300">Ancient ruins whispering the echoes of myth and civilisation. A UNESCO World Heritage site and the mythological birthplace of Apollo and Artemis, this sacred island offers an unparalleled journey through Greek history and mythology.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/28574638/pexels-photo-28574638/free-photo-of-beautiful-coastal-view-of-cycladic-village.jpeg?auto=compress&cs=tinysrgb&w=600" 
                              alt="Amorgos" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Amorgos</h4>
                            <p className="text-sand-300">A vertical world rising from the deep, where time slows. Famous for its dramatic cliff-side monastery of Hozoviotissa and crystal-clear waters, Amorgos offers a more authentic Greek island experience away from the tourist crowds.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/275591/pexels-photo-275591.jpeg?auto=compress&cs=tinysrgb&w=600" 
                              alt="Rhodes" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Rhodes</h4>
                            <p className="text-sand-300">A labyrinth of fortresses. A living history book. The largest of the Dodecanese islands features a remarkably preserved medieval old town, ancient ruins, and beautiful beaches—a perfect blend of history and natural beauty.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/17635102/pexels-photo-17635102/free-photo-of-crystal-beach.jpeg?auto=compress&cs=tinysrgb&w=600" 
                              alt="Secret Islands" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Secret Islands</h4>
                            <p className="text-sand-300">Revealing secrets of hidden jewels. Our journey will include stops at lesser-known islands and secluded coves accessible only by boat, offering authentic experiences far from the standard tourist routes.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/3601426/pexels-photo-3601426.jpeg" 
                              alt="Bonjuk Bay" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Bonjuk Bay</h4>
                            <p className="text-sand-300">Immerse yourself in its breathtaking beauty and uncover the magic this wondrous realm holds. This secluded bay on Turkey's Aegean coast transforms during the Per Anhalter Festival into a vibrant community of creators, artists, and seekers.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Day-by-Day Journey</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-sand-100">Day 1-4: Sailing the Greek Islands</h4>
                          <p className="text-sand-300">Island hopping through the Cyclades, exploring ancient ruins, swimming in crystal clear waters, and enjoying meals prepared by our Michelin-starred chef.</p>
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-sand-100">Day 5: Transit to Per Anhalter Festival</h4>
                          <p className="text-sand-300">Journey from Rhodes to Bonjuk Bay, Turkey, and settle into the festival atmosphere.</p>
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-sand-100">Day 5-8: Per Anhalter Festival</h4>
                          <p className="text-sand-300">Immerse in a world of creative "flowshops," music performances, art installations, and community connections.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="portal" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">The Portal of Wonders</h2>
                  <div className="space-y-6 text-sand-300">
                    <p className="italic text-xl">
                      Step through the portal of Inter-dimensional Mind & Body Nourishment, Galactic Connections, Stellar Celebrations and other Ethereal Surprises.
                    </p>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Per Anhalter Festival</h3>
                      <p className="mb-4">
                        The second phase of our journey takes place at the Per Anhalter Festival in Bonjuk Bay, Turkey - a unique gathering that creates a portal between dimensions. Here, you'll experience:
                      </p>
                      <ul className="list-disc list-inside space-y-2">
                        <li>Numerous spiritual and artistic "flowshops"</li>
                        <li>Live music performances across multiple stages</li>
                        <li>Art installations that transform the landscape</li>
                        <li>A vibrant community of fellow travelers</li>
                        <li>Transformative experiences in a stunning natural setting</li>
                      </ul>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Join Our Email List for Future Journeys</h3>
                      <p className="mb-6">
                        Sign up to our portal newsletter to receive updates about future journeys and special offerings from Space-Time Architects.
                      </p>
                      <div className="flex justify-center">
                        <Button
                          onClick={() => window.open("http://eepurl.com/i-ZWPc", "_blank")}
                          className="px-6 py-3"
                        >
                          Enter The Portal
                        </Button>
                      </div>
                    </div>
                    
                    <img 
                      src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/Space%20Time%20Architects.jpg" 
                      alt="Portal of Wonders" 
                      className="w-full h-auto rounded-lg mt-6"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="accommodation" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Accommodation Options</h2>
                  <p className="text-sand-300 mb-8">
                    Our journey features two distinct accommodation experiences: luxurious sailing accommodations during the Greek Islands portion, and your choice of accommodations during the festival portion in Turkey.
                  </p>
                  
                  {/* Image Gallery */}
                  <div className="relative rounded-lg overflow-hidden aspect-video mb-8">
                    <img 
                      src={accommodationImages[currentImageIndex]}
                      alt="Accommodation"
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Navigation Arrows */}
                    <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4">
                      <button
                        onClick={(e) => { e.preventDefault(); prevImage(); }}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      
                      <button
                        onClick={(e) => { e.preventDefault(); nextImage(); }}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                    </div>
                    
                    {/* Image Indicators */}
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                      {accommodationImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full ${currentImageIndex === index ? 'bg-white' : 'bg-white/50'}`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-8">
                    {/* Sailing Accommodation Details - No separate image now */}
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-2xl font-display mb-4">During Sailing (26 May - 30 May)</h3>
                      <div className="flex items-center gap-2 text-sand-300 mb-4">
                        <Anchor className="h-5 w-5" />
                        <span>Private cabin on a luxurious catamaran</span>
                      </div>
                      <p className="text-sand-300 mb-4">
                        During the sailing portion, you'll stay aboard our premium catamaran featuring spacious cabins with ensuite bathrooms, ample deck space for relaxation, and all the comforts needed for a luxurious journey at sea.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2 text-sand-300">
                          <BedDouble className="h-4 w-4" />
                          <span>Private Cabins</span>
                        </div>
                        <div className="flex items-center gap-2 text-sand-300">
                          <Coffee className="h-4 w-4" />
                          <span>Ensuite Bathrooms</span>
                        </div>
                        <div className="flex items-center gap-2 text-sand-300">
                          <Utensils className="h-4 w-4" />
                          <span>Chef-Prepared Meals</span>
                        </div>
                        <div className="flex items-center gap-2 text-sand-300">
                          <Sunset className="h-4 w-4" />
                          <span>Spacious Sundeck</span>
                        </div>
                      </div>
                    </div>

                    {/* Festival Accommodation Options */}
                    <h3 className="text-xl font-display mb-4">During Per Anhalter Festival (30 May - 2 June)</h3>
                    
                    {/* Option 1: Catamaran - Wide image with text below */}
                    <div className="bg-earth-800/50 rounded-lg overflow-hidden">
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/20240229-untitled-16-19-59-2-Enhanced-NR.jpg" 
                        alt="Anchored Catamaran" 
                        className="w-full h-64 object-cover"
                      />
                      <div className="p-6">
                        <h4 className="text-xl font-display mb-2">Option 1: Catamaran Stay</h4>
                        <p className="text-sand-300 mb-4">
                          Your cabin remains yours throughout the festival. A shuttle will bring you between the boat and festival area, giving you the comfort of a familiar space while enjoying all festival activities.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-2 text-sand-300">
                            <Ship className="h-4 w-4" />
                            <span>Familiar Accommodation</span>
                          </div>
                          <div className="flex items-center gap-2 text-sand-300">
                            <MapPin className="h-4 w-4" />
                            <span>Shuttle Service Included</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Option 2: Bungalow/Tent - Wide image with text below */}
                    <div className="bg-earth-800/50 rounded-lg overflow-hidden">
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/Bonjuk%20Camping.png" 
                        alt="Luxury Tent" 
                        className="w-full h-64 object-cover"
                      />
                      <div className="p-6">
                        <h4 className="text-xl font-display mb-2">Option 2: Bungalow/Tent</h4>
                        <p className="text-sand-300 mb-4">
                          Bungalows and tents available at Bonjuk Bay (extra cost applies). This option places you directly at the festival site, allowing for full immersion in the Per Anhalter experience.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-2 text-sand-300">
                            <Tent className="h-4 w-4" />
                            <span>On-site Accommodation</span>
                          </div>
                          <div className="flex items-center gap-2 text-sand-300">
                            <Star className="h-4 w-4" />
                            <span>Full Festival Immersion</span>
                          </div>
                        </div>
                        <p className="text-sm text-sand-400 mt-4">
                          View accommodation options at <a href="https://bonjukbay.com/accommodation" target="_blank" rel="noopener noreferrer" className="underline">bonjukbay.com/accommodation</a>
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg mt-6">
                      <h3 className="text-xl font-display mb-4">Dining Experience</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-sand-100 mb-2">Onboard</h4>
                          <p className="text-sand-300">All meals are included while sailing, curated by a three-star Michelin chef who will prepare a variety of Mediterranean-inspired dishes using fresh, local ingredients.</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-sand-100 mb-2">At the Festival</h4>
                          <p className="text-sand-300">Meals available at an additional cost (€60 per day), featuring a range of options to suit all dietary preferences.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="location" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Journey Route</h2>
                  <div className="space-y-8">
                    <div className="bg-earth-800/50 p-6 rounded-lg mb-6">
                      <p className="text-sand-300 mb-4">
                        Our Aegean Odyssey takes you on a carefully crafted route through the most stunning locations in the Aegean Sea. Follow our journey chronologically from Mykonos (1) through the islands to Bonjuk Bay (5).
                      </p>
                      <p className="text-sand-300">
                        Click on each numbered marker on the map to learn more about each destination.
                      </p>
                    </div>
                    
                    <div className="aspect-[16/9] rounded-lg overflow-hidden">
                      <div id="venue-map" className="w-full h-full rounded-lg"></div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">1</span>
                          Mykonos, Greece
                        </h3>
                        <p className="text-sand-300">
                          Our journey begins in cosmopolitan Mykonos with its iconic windmills and vibrant culture. We'll gather here before boarding our premium catamaran.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">2</span>
                          Delos, Greece
                        </h3>
                        <p className="text-sand-300">
                          Just a short sail from Mykonos lies the archaeological wonderland of Delos, birthplace of Apollo and one of the most important mythological sites in Greece.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">3</span>
                          Amorgos, Greece
                        </h3>
                        <p className="text-sand-300">
                          Known for its dramatic cliffs and the famous monastery of Hozoviotissa, Amorgos offers breathtaking landscapes and crystal-clear waters.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">4</span>
                          Rhodes, Greece
                        </h3>
                        <p className="text-sand-300">
                          The largest of the Dodecanese islands, Rhodes features a medieval Old Town, ancient ruins, and beautiful beaches before we transition to Turkey.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg md:col-span-2">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">5</span>
                          Bonjuk Bay, Turkey
                        </h3>
                        <p className="text-sand-300">
                          Our final destination is this magical bay on Turkey's Aegean coast, where the Per Anhalter Festival creates a unique atmosphere of creativity, music, and connection.
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Travel Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-sand-100 mb-2">Getting There</h4>
                          <ul className="space-y-2 text-sand-300">
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>Fly to Athens or directly to Mykonos International Airport (JMK)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>Return from Dalaman or Bodrum Airports in Turkey</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>Airport transfers included</span>
                            </li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-sand-100 mb-2">Visa Requirements</h4>
                          <ul className="space-y-2 text-sand-300">
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>Check visa requirements for both Greece and Turkey</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>Many nationalities can obtain e-Visas for Turkey online</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>We'll provide guidance after booking</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="faq" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-6">
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What makes this a dual experience?</h3>
                      <p className="text-sand-300">
                        This unique journey combines two extraordinary experiences: first, a luxury sailing adventure through the iconic Greek Islands aboard a premium catamaran; second, an immersive festival experience at the Per Anhalter Festival in Turkey, which blends art, music, and spiritual practices in a stunning natural setting. This combination offers both relaxation and adventure on the water, followed by connection and celebration at the festival.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">Do I need sailing experience?</h3>
                      <p className="text-sand-300">
                        No sailing experience is necessary. Our professional captain and crew handle all technical aspects of sailing. You're welcome to participate and learn as much as you'd like, or simply relax and enjoy the journey. This experience is designed to be accessible to everyone, regardless of prior sailing knowledge.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What should I pack for both parts of the journey?</h3>
                      <p className="text-sand-300">
                        For the sailing portion: lightweight clothing, swimwear, sun protection (hat, sunglasses, sunscreen), a light jacket for evenings, and non-marking deck shoes. For the festival: comfortable clothing for both day and night (temperatures can vary), festival attire if desired, comfortable shoes for dancing, and any personal items for workshop participation. We'll provide a detailed packing list upon booking.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">How do we get from the sailing portion to the festival?</h3>
                      <p className="text-sand-300">
                        We'll handle all the logistics of this transition. After completing our sailing journey, we'll dock in Rhodes where transport will be arranged to take us to Bonjuk Bay in Turkey (approximately a 3-hour journey including the ferry crossing). All transportation during this transition is included in your booking.
                      </p>
                    </div>

                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What is the Per Anhalter Festival?</h3>
                      <p className="text-sand-300">
                        The Per Anhalter Festival is a boutique gathering that blends art, music, and conscious community in a beautiful natural setting. It features numerous "flowshops" (creative and spiritual workshops), live music performances across multiple stages, art installations, and community activities. The festival has a strong focus on sustainability, personal growth, and creating meaningful connections between participants.
                      </p>
                    </div>

                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What is the cancellation policy?</h3>
                      <p className="text-sand-300">
                        We offer a full refund if canceled 90 days before departure, 50% refund if canceled 60-89 days before departure, and no refund for cancellations less than 60 days before departure. We strongly recommend comprehensive travel insurance that includes cancellation coverage.
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="sticky top-24 space-y-6">
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-display">Price</h3>
                    <div className="text-2xl font-display">€5,500pp</div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sand-300">
                      <Clock className="h-5 w-5 text-sand-400" />
                      <span>8 days, 7 nights</span>
                    </div>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-5 w-5 text-sand-400" />
                      <span>May 26 - June 2, 2025</span>
                    </div>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Users className="h-5 w-5 text-sand-400" />
                      <span>Limited to 16 participants</span>
                    </div>
                    <Button
                      className="w-full mt-4"
                      onClick={handlePurchaseClick}
                    >
                      Request to Book
                    </Button>
                  </div>
                </div>

                {/* Creator Information - Added similar to Xamanic page */}
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Creator</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/Tile%20Flyer.png" 
                      alt="Space Time Architects"
                      className="w-16 h-16 rounded-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "https://api.dicebear.com/7.x/initials/svg?seed=STA&backgroundColor=1d1918&textColor=e8e4dc";
                      }}
                    />
                    <div>
                      <p className="text-sand-100 font-medium">Space Time Architects</p>
                      <p className="text-sand-400 text-sm">Experience Creators</p>
                    </div>
                  </div>
                  <p className="text-sand-300 text-sm mb-4">
                    Space Time Architects designs transformative experiences that blend adventure, culture, and meaningful connection. Specializing in unique journeys that combine sailing, festivals, and exploration, they create moments that transcend ordinary travel.
                  </p>
                  <a 
                    href="https://space-time.travel" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sand-300 hover:text-sand-200 transition-colors"
                  >
                    <Globe className="h-4 w-4" />
                    <span className="underline">space-time.travel</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">What's Included</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Ship className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">7 days aboard luxury catamaran</span>
                    </li>                    
                    <li className="flex items-start gap-3">
                      <Utensils className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">All meals during sailing by three-star Michelin chef</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <BedDouble className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Accommodation on catamaran</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">All transfers and transport</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Anchor className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Professional crew and guides</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Not Included</h3>
                  <ul className="space-y-3 text-sand-300">
                    <li className="flex items-start gap-3">
                      <span className="text-sand-100">•</span> 
                      <span>Flights to Mykonos and from Izmir</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-sand-100">•</span>
                      <span className="text-sand-300">Ticket for 3 days at Per Anhalter Festival</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-sand-100">•</span> 
                      <span>Meals during the festival (€60/day meal plan available)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-sand-100">•</span> 
                      <span>Bungalow/tent accommodation at festival (optional upgrade)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-sand-100">•</span> 
                      <span>Travel insurance (strongly recommended)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="mt-24 bg-earth-800/50 p-8 rounded-lg text-center">
            <h2 className="text-3xl font-display mb-6">Begin Your Aegean Odyssey</h2>
            <p className="text-sand-300 mb-8 max-w-2xl mx-auto">
              This unique dual experience combines luxury sailing through the Greek Islands with the transformative Per Anhalter Festival in Turkey. Limited spaces available for this exclusive journey.
            </p>
            <Button 
              size="lg"
              onClick={handlePurchaseClick}
              className="text-lg px-8 py-4"
            >
              Request to Book
            </Button>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="sign-up"
        redirectTo={location.pathname}
      />
    </div>
  );
}